import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { CopyWithSourcesButton } from "@/components/citations/CopyWithSourcesButton";
import { hasCopyableSources } from "@/lib/copyWithSources";
import { isTopModal, useModalStack } from "@/lib/modalStack";
import { useFocusTrap } from "@/lib/useFocusTrap";
import { useChatStore } from "@/runtime/chatStore";

const MODAL_ID = "artifact-panel";

const ARTIFACT_MARKDOWN_COMPONENTS = {
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="type-body-lg my-[var(--space-4)] text-[var(--text-primary)] first:mt-0 last:mb-0">{children}</p>
  ),
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="type-h2 mt-[var(--space-6)] mb-[var(--space-3)] text-[var(--text-primary)] first:mt-0">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="type-h3 mt-[var(--space-5)] mb-[var(--space-2)] text-[var(--text-primary)] first:mt-0">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="type-title mt-[var(--space-4)] mb-[var(--space-2)] text-[var(--text-primary)] first:mt-0">
      {children}
    </h3>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="my-[var(--space-4)] ml-[var(--space-5)] list-disc space-y-[var(--space-2)]">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="my-[var(--space-4)] ml-[var(--space-5)] list-decimal space-y-[var(--space-2)]">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="type-body-lg text-[var(--text-primary)] marker:text-[var(--text-tertiary)]">{children}</li>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[var(--accent-text)] underline underline-offset-2 transition-[text-decoration-thickness] duration-[var(--dur-fast)] hover:decoration-2"
    >
      {children}
    </a>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="my-[var(--space-5)] overflow-x-auto rounded-[var(--radius-lg)] shadow-[var(--elev-ring)]">
      <table className="type-body-sm min-w-full border-collapse text-[var(--text-primary)]">{children}</table>
    </div>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="type-body-sm border-b border-[var(--border-default)] bg-[var(--surface-sunken)] px-[var(--space-3)] py-[var(--space-2)] text-left font-semibold text-[var(--text-secondary)]">
      {children}
    </th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="type-body-sm border-b border-[var(--border-default)] px-[var(--space-3)] py-[var(--space-2)] text-[var(--text-primary)]">
      {children}
    </td>
  ),
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="type-body-lg my-[var(--space-4)] border-l-2 border-[var(--border-strong)] pl-[var(--space-4)] italic text-[var(--text-secondary)]">
      {children}
    </blockquote>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-[var(--text-primary)]">{children}</strong>
  ),
  hr: () => <hr className="my-[var(--space-6)] border-0 border-t border-[var(--border-default)]" />,
  // react-markdown v10 has no `inline` prop (see MessageRenderer's CodeBlock):
  // every backtick is a <code>, and fenced blocks are additionally wrapped in
  // <pre>. So style <code> as the inline chip and let <pre> reset it.
  code: ({ children }: { children?: React.ReactNode }) => (
    <code className="type-code rounded-[var(--radius-sm)] bg-[var(--surface-sunken)] px-[var(--space-1)] py-0.5 text-[var(--text-primary)]">
      {children}
    </code>
  ),
  pre: ({ children }: { children?: React.ReactNode }) => (
    <pre className="type-code my-[var(--space-4)] overflow-x-auto rounded-[var(--radius-lg)] bg-[var(--surface-sunken)] p-[var(--space-4)] text-[var(--text-primary)] shadow-[var(--elev-ring)] [&>code]:bg-transparent [&>code]:p-0">
      {children}
    </pre>
  ),
};

function findMessage(messages: ReturnType<typeof useChatStore.getState>["messages"], id: string | null) {
  if (!id) return null;
  return messages.find((m) => m.id === id) ?? null;
}

function titleFor(content: string): string {
  const trimmed = content.trim();
  const firstLine = trimmed.split("\n")[0] ?? "";
  const stripped = firstLine.replace(/^#+\s*/, "").trim();
  if (stripped.length > 60) return stripped.slice(0, 57) + "…";
  return stripped || "Untitled response";
}

export function ArtifactPanel() {
  const activeId = useChatStore((s) => s.activeArtifactMessageId);
  const setActive = useChatStore((s) => s.setActiveArtifactMessageId);
  const messages = useChatStore((s) => s.messages);
  const message = findMessage(messages, activeId);
  const [copied, setCopied] = useState(false);
  const dialogRef = useRef<HTMLElement | null>(null);
  const isOpen = message !== null;
  useModalStack(MODAL_ID, isOpen);
  useFocusTrap(dialogRef, isOpen);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && isTopModal(MODAL_ID)) setActive(null);
    }
    if (message) {
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }
    return undefined;
  }, [message, setActive]);

  useEffect(() => {
    setCopied(false);
  }, [activeId]);

  if (!message) return null;

  async function onCopy() {
    if (!message) return;
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard write may fail in insecure contexts; user can copy manually */
    }
  }

  function onDownload() {
    if (!message) return;
    const blob = new Blob([message.content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${titleFor(message.content).replace(/[^\w\s-]/g, "").slice(0, 40)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const title = titleFor(message.content);

  return (
    <>
      <div
        className="bk-anim-backdrop fixed inset-0 z-40 bg-[var(--scrim)]"
        onClick={() => setActive(null)}
        aria-hidden="true"
      />
      <aside
        ref={dialogRef}
        role="dialog"
        aria-label="Artifact"
        className="bk-anim-drawer fixed right-0 top-0 z-50 flex h-full w-full flex-col border-l border-[var(--border-default)] bg-[var(--surface-raised)] shadow-[var(--elev-whisper-xl)] sm:max-w-2xl"
      >
        <header className="flex items-center gap-[var(--space-3)] border-b border-[var(--border-default)] px-[var(--space-5)] py-[var(--space-3)]">
          <div className="flex-1 truncate">
            <div className="type-overline text-[var(--text-tertiary)]">
              Artifact
            </div>
            <div className="type-title truncate text-[var(--text-primary)]">{title}</div>
          </div>
          <button
            type="button"
            onClick={onCopy}
            className="type-caption rounded-[var(--radius-pill)] bg-[var(--surface-raised)] px-[var(--space-3)] py-[var(--space-1)] text-[var(--text-secondary)] shadow-[var(--elev-ring)] transition-[background-color,box-shadow,color] duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:text-[var(--text-primary)] hover:shadow-[var(--elev-ring-strong),var(--elev-whisper)] focus-visible:shadow-[var(--elev-focus)] focus-visible:outline-none"
          >
            {copied ? "Copied" : "Copy"}
          </button>
          {hasCopyableSources(message.citations) && (
            <CopyWithSourcesButton
              content={message.content}
              citations={message.citations}
              className="type-caption rounded-[var(--radius-pill)] bg-[var(--surface-raised)] px-[var(--space-3)] py-[var(--space-1)] text-[var(--text-secondary)] shadow-[var(--elev-ring)] transition-[background-color,box-shadow,color] duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:text-[var(--text-primary)] hover:shadow-[var(--elev-ring-strong),var(--elev-whisper)] focus-visible:shadow-[var(--elev-focus)] focus-visible:outline-none"
            />
          )}
          <button
            type="button"
            onClick={onDownload}
            className="type-caption rounded-[var(--radius-pill)] bg-[var(--surface-raised)] px-[var(--space-3)] py-[var(--space-1)] text-[var(--text-secondary)] shadow-[var(--elev-ring)] transition-[background-color,box-shadow,color] duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:text-[var(--text-primary)] hover:shadow-[var(--elev-ring-strong),var(--elev-whisper)] focus-visible:shadow-[var(--elev-focus)] focus-visible:outline-none"
          >
            Download
          </button>
          <button
            type="button"
            onClick={() => setActive(null)}
            aria-label="Close"
            className="rounded-[var(--radius-pill)] p-[var(--space-2)] text-[var(--text-tertiary)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)] focus-visible:shadow-[var(--elev-focus)] focus-visible:outline-none"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
              <path d="M3.5 3.5l7 7M10.5 3.5l-7 7" />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-[var(--space-6)] py-[var(--space-5)]">
          <div className="type-body-lg mx-auto max-w-[var(--reading-measure)] text-[var(--text-primary)]">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={ARTIFACT_MARKDOWN_COMPONENTS}>
              {message.content}
            </ReactMarkdown>
          </div>
        </div>
      </aside>
    </>
  );
}
